import React, { useState, useEffect, useRef } from 'react'
import styles from './Caller.module.css'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import { useContext } from 'react'
import { humanReadableTimePassed } from '../../Helper/helpers'


const PhoneIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.44 2 2 0 0 1 3.6 1.28h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.85a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
)

const ChevronLeft = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
)

const ChevronRight = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
)

const DEFAULT_CONTACTS = [
    { fullName: "Navneet Chandan", phoneNumber: "9999648406", jobIdentifier: "JOB-2847", createdOn: '' },
    { fullName: "Navneet Kumar", phoneNumber: "9319408406", jobIdentifier: "JOB-2848", createdOn: '' },
]

const formatNumber = (num) => {
    const clean = String(num).replace(/\D/g, '')
    if (clean.length === 10) return `${clean.slice(0, 5)} ${clean.slice(5)}`
    return num
}

const getInitials = (name) =>
    name?.split(' ')?.map(w => w[0])?.join('').slice(0, 2).toUpperCase()

const Caller = () => {
    const [index, setIndex] = useState(0)
    const [callState, setCallState] = useState('idle')
    const [slideDir, setSlideDir] = useState(null)   // 'left' | 'right' | null
    const [animKey, setAnimKey] = useState(0)
    const timeoutRef = useRef(null)
    const authCtx = useContext(AuthContext)
    const [contacts, setContacts] = useState(DEFAULT_CONTACTS)
    const [fetchingStatus, setFetchingStatus] = useState('idle') // 'idle' | 'fetching' | 'error'

    const getCallerContacts = async () => {
        setFetchingStatus('fetching')
        const response = await fetch(BASE_URL_API + "/getCallerContacts",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    executiveIdentifier: authCtx.userIdentifier,
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error");
        } else {
            const responseData = await response.json();
            if (responseData.status === "success") {
                if (responseData?.response?.length === 0) {
                    setContacts(DEFAULT_CONTACTS)
                } else {
                    setContacts(responseData.response)
                }
                setIndex(0)
            } else {
                if (responseData.message === "token Expired, please login again!") {
                    authCtx.logout()
                } else {
                    setContacts(DEFAULT_CONTACTS)
                    setFetchingStatus('error')
                }
            }
            console.log(responseData)
        }
        setFetchingStatus('idle')
    }

    const contact = contacts[index]
    const total = contacts.length

    const navigate = (dir) => {
        if (slideDir) return
        setSlideDir(dir)
        setCallState('idle')
        clearTimeout(timeoutRef.current)

        setTimeout(() => {
            setIndex(i =>
                dir === 'right'
                    ? (i + 1) % total
                    : (i - 1 + total) % total
            )
            setAnimKey(k => k + 1)
            setSlideDir(null)
        }, 220)
    }

    const handleCall = () => {
        if (callState === 'dialing') return
        setCallState('dialing')
        window.location.href = `tel:${contact?.phoneNumber || contact?.number || ''}`
        timeoutRef.current = setTimeout(() => setCallState('idle'), 3000)
    }

    useEffect(() => () => clearTimeout(timeoutRef.current), [])

    const slideClass =
        slideDir === 'right' ? styles.slideOutLeft
            : slideDir === 'left' ? styles.slideOutRight
                : ''

    useEffect(() => {
        getCallerContacts()
    }, [])

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.orb1} />
            <div className={styles.orb2} />

            {/* ── Prev button ── */}
            <button
                className={`${styles.navBtn} ${styles.navBtnLeft}`}
                onClick={() => navigate('left')}
                disabled={!!slideDir}
                aria-label="Previous contact"
            >
                <ChevronLeft />
            </button>

            {/* ── Card ── */}
            <div key={animKey} className={`${styles.card} ${slideClass}`}>
                <div className={styles.timeAgo}>
                    {humanReadableTimePassed(contact?.createdOn)}
                </div>

                {/* Job badge */}
                <div className={styles.jobBadge}>
                    <span className={styles.jobDot} />
                    {contact?.jobIdentifier}
                </div>

                {/* Avatar */}
                <div className={styles.avatarRing}>
                    <div className={styles.avatar}>{getInitials(contact?.fullName)}</div>
                </div>

                {/* Name */}
                <h1 className={styles.name}>{contact?.fullName}</h1>

                {/* Number */}
                <div className={styles.numberRow}>
                    <span className={styles.countryCode}>+91</span>
                    <span className={styles.number}>{formatNumber(contact?.phoneNumber || contact?.number || '')}</span>
                </div>

                {/* Divider */}
                <div className={styles.divider} />

                {/* Call button */}
                <div className={styles.callArea}>
                    {callState === 'dialing' && (
                        <>
                            <div className={`${styles.ring} ${styles.ring1}`} />
                            <div className={`${styles.ring} ${styles.ring2}`} />
                            <div className={`${styles.ring} ${styles.ring3}`} />
                        </>
                    )}
                    <button
                        className={`${styles.callBtn} ${callState === 'dialing' ? styles.calling : ''}`}
                        onClick={handleCall}
                        aria-label={`Call ${contact?.fullName}`}
                    >
                        <PhoneIcon />
                    </button>
                </div>

                <p className={styles.hint}>
                    {callState === 'dialing' ? 'Opening dialer…' : 'Tap to call'}
                </p>

                {/* Pagination dots */}
                <div className={styles.dots}>
                    {contacts.map((_, i) => (
                        <span
                            key={i}
                            className={`${styles.dot} ${i === index ? styles.dotActive : ''}`}
                        />
                    ))}
                </div>
            </div>

            {/* ── Next button ── */}
            <button
                className={`${styles.navBtn} ${styles.navBtnRight}`}
                onClick={() => navigate('right')}
                disabled={!!slideDir}
                aria-label="Next contact"
            >
                <ChevronRight />
            </button>

            <div className={styles.refreshWrapper}>
                {fetchingStatus === 'idle' &&
                    <button onClick={() => getCallerContacts()} className={styles.refreshBtn}>
                        Refresh List
                    </button>
                }
                {fetchingStatus === 'fetching' &&
                    <button className={styles.refreshBtn} disabled>
                        Fetching...
                    </button>
                }

            </div>
        </div>
    )
}

export default Caller